import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  searchContacts, createContact, createProspectRecord,
  fetchContactDetails, fetchCallHistory, fetchContactUpdates,
  createActivityLogEntry, createSubitem,
} from '../api/monday';
import { BROKERS } from '../config/brokers';

// ── Design tokens (match Queue.js) ────────────────────────────────────────
const C = {
  indigo:        '#534AB7',
  indigoDark:    '#3C3489',
  indigoLight:   '#EEEDFE',
  teal:          '#1D9E75',
  tealLight:     '#E1F5EE',
  tealDark:      '#085041',
  amber:         '#633806',
  amberLight:    '#FAEEDA',
  red:           '#791F1F',
  redLight:      '#FCEBEB',
  bgCard:        '#ffffff',
  bgSoft:        '#f0efe9',
  border:        '#e8e6e0',
  textPrimary:   '#111827',
  textSecondary: '#6b7280',
  textTertiary:  '#9ca3af',
};

const PROSPECT_TYPES = [
  { label: 'Buyer / Tenant Rep',  value: 'Ext Requirement'      },
  { label: 'Listing / Seller Rep', value: 'Int Requirement'      },
  { label: 'Off Market',           value: 'Off Market / Pocket'  },
];

const PRIORITIES = [
  { label: 'Urgent',    value: '(1) Urgent',    color: '#991B1B', bg: '#FEE2E2' },
  { label: 'Motivated', value: '(2) Motivated', color: '#C2410C', bg: '#FFEDD5' },
  { label: 'Active',    value: '(3) Active',    color: '#A16207', bg: '#FEF9C3' },
];

const ACTIVITY_TYPES = ['Prospect call', 'Owner call', 'Other'];
const OUTCOMES = ['Reached', 'No answer / left message'];
const OUTCOME_COLORS = {
  'Reached':                  { bg: '#E1F5EE', color: '#085041' },
  'No answer / left message': { bg: '#f3f4f6', color: '#6b7280' },
};

// Monday contact board options (from cheat sheet)
const LEAD_SOURCES = [
  'Apollo','CCIM Referral','CIG Referral','CityFeet','Commercial Search',
  'Craigslist','Crexi','Data Analysis / Research Yield','E-mail List',
  'EPropertyPush','Event Attendee','External Broker','Facebook','GVG',
  'High Networth','Inbound Call','Inbound Email','Internet Search','IOS List',
  'List Self Storage','Listing / Signage Call','Local Broker',
  'LoopNet - Banner Ad','LoopNet - Listing Response','Market Watch - Attendee',
  'Market Watch - Word of Mouth','Missoula Economic Partnership Referral',
  'Network/Friend','Not a Lead','OfficeSpace.com','Other','Personal Interaction',
  'Print','Property Shark','Prospecting','RCM','Referral','Reonomy',
  'Repeat Client','SIOR Referrral','State Official','Unknown',
  'X (formerly Twitter)',
];

const CONTACT_TYPES = [
  'Acquisition Manager','Architect','Attorney','Broker','Capital Advisor',
  'City/County Official','Client','CRE Investor','Director of Investments',
  'Land Owner','Landlord','Lead','Legal','Lending','Manager','Media',
  'Other','President','Principal','Property Developer','Realty Specialist',
  'Specialty Consultant','Tenant','Title/Escrow','Vendor',
  'Vice President Investments',
];

const PERSONAS = [
  'CRE Investor','Business Owner','Land Owner','Landlord','Other',
  'Property Developer','Real Estate Professional-Agent-Broker',
  'Related Industry-Referral Source',
];

const INTERESTS_OPTIONS = ['Industrial','Land','Multifamily','Office','Retail','Self Storage'];
const MARKETS_OPTIONS   = ['Billings','Bitterroot','Butte','Flathead Valley','Great Falls','Helena','Missoula'];
const CONTENT_PREFS     = [
  'Market Reports and Insights',
  'Commercial Listings',
  'Print Market Reports and Newsletters by Mail',
];

function defaultFollowUp() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Shared input styles ────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '11px 13px', fontSize: '14px',
  borderRadius: '9px', border: `1.5px solid ${C.border}`,
  outline: 'none', backgroundColor: C.bgSoft,
  color: C.textPrimary, marginBottom: '12px',
};

const labelStyle = {
  fontSize: '11px', fontWeight: '700', color: C.textSecondary,
  letterSpacing: '0.04em', textTransform: 'uppercase',
  marginBottom: '6px', marginTop: 0, display: 'block',
};

// ── Reusable prospect type + priority + date + requirements fields ─────────
function QueueFields({ form, onChange }) {
  return (
    <>
      <label style={labelStyle}>Prospect Type *</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
        {PROSPECT_TYPES.map((pt) => (
          <button
            key={pt.value}
            onClick={() => onChange('prospectType', pt.value)}
            style={{
              padding: '11px 14px', borderRadius: '10px', textAlign: 'left',
              border: `1.5px solid ${form.prospectType === pt.value ? C.indigo : C.border}`,
              backgroundColor: form.prospectType === pt.value ? C.indigoLight : C.bgCard,
              color: form.prospectType === pt.value ? C.indigoDark : C.textPrimary,
              fontWeight: form.prospectType === pt.value ? '600' : '400',
              cursor: 'pointer', fontSize: '14px',
            }}
          >
            {pt.label}
          </button>
        ))}
      </div>

      <label style={labelStyle}>Priority *</label>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {PRIORITIES.map((p) => (
          <button
            key={p.value}
            onClick={() => onChange('priority', p.value)}
            style={{
              flex: 1, padding: '10px 4px', borderRadius: '10px', fontSize: '12px',
              border: `1.5px solid ${form.priority === p.value ? p.color : C.border}`,
              backgroundColor: form.priority === p.value ? p.bg : C.bgCard,
              color: form.priority === p.value ? p.color : C.textSecondary,
              fontWeight: form.priority === p.value ? '700' : '400',
              cursor: 'pointer',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <label style={labelStyle}>First Follow-Up Date *</label>
      <input
        type="date"
        style={{ ...inputStyle, marginBottom: '16px' }}
        value={form.nextFollowUp}
        onChange={(e) => onChange('nextFollowUp', e.target.value)}
      />

      {/* Assign to teammate */}
      <label style={labelStyle}>Assign To</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
        {BROKERS.map((b) => {
          const sel = form.assignTo === b.name;
          return (
            <button
              key={b.name}
              onClick={() => onChange('assignTo', b.name)}
              style={{
                padding: '7px 13px', borderRadius: '20px', fontSize: '12px',
                fontWeight: sel ? '700' : '400', cursor: 'pointer',
                border: `1.5px solid ${sel ? C.indigo : C.border}`,
                backgroundColor: sel ? C.indigoLight : C.bgCard,
                color: sel ? C.indigoDark : C.textSecondary,
              }}
            >
              {b.name.split(' ')[0]}
            </button>
          );
        })}
      </div>

      {/* Requirements — collapsible */}
      <div
        onClick={() => onChange('showRequirements', !form.showRequirements)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
          border: `1.5px dashed ${form.showRequirements ? C.indigo : C.border}`,
          backgroundColor: form.showRequirements ? C.indigoLight : 'transparent',
          marginBottom: form.showRequirements ? '12px' : '4px',
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: '600', color: form.showRequirements ? C.indigoDark : C.textTertiary }}>
          {form.showRequirements ? '▲ Hide Requirements' : '＋ Add Requirements (SF / Acres / Price)'}
        </span>
      </div>

      {form.showRequirements && (
        <div style={{ padding: '4px 0 8px' }}>
          <label style={labelStyle}>Square Feet</label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Min SF" value={form.sfMin} onChange={(e) => onChange('sfMin', e.target.value)} />
            <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Max SF" value={form.sfMax} onChange={(e) => onChange('sfMax', e.target.value)} />
          </div>
          <label style={labelStyle}>Acres</label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Min Acres" value={form.acresMin} onChange={(e) => onChange('acresMin', e.target.value)} />
            <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Max Acres" value={form.acresMax} onChange={(e) => onChange('acresMax', e.target.value)} />
          </div>
          <label style={labelStyle}>Price</label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
            <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Min $" value={form.priceMin} onChange={(e) => onChange('priceMin', e.target.value)} />
            <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Max $" value={form.priceMax} onChange={(e) => onChange('priceMax', e.target.value)} />
          </div>
        </div>
      )}
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function AddProspectModal({ onClose, onAdded, brokerName }) {
  // 'search' | 'found' | 'new' | 'add-to-queue'
  const [step, setStep] = useState('search');

  // Search state
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [searching, setSearching]   = useState(false);
  const [searched, setSearched]     = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Rich contact detail state (for "found" step)
  const [contactDetail, setContactDetail]   = useState(null);
  const [callHistory, setCallHistory]       = useState([]);
  const [contactNotes, setContactNotes]     = useState([]);
  const [loadingDetail, setLoadingDetail]   = useState(false);

  // Inline log form state
  const [showLogForm, setShowLogForm]       = useState(false);
  const [logSaving, setLogSaving]           = useState(false);
  const [logSuccess, setLogSuccess]         = useState(false);
  const [logForm, setLogForm]               = useState({ activityType: '', outcome: '', notes: '' });

  // Add-to-queue toggle on found screen
  const [showQueueForm, setShowQueueForm]   = useState(false);

  // Form state
  const [form, setForm] = useState({
    // Contact fields
    name: '', phone: '', email: '', company: '', title: '',
    // Marketing fields
    leadSource: '', contactType: '', persona: [],
    interests: [], markets: [], contentPreferences: [],
    // Queue fields
    addToQueue: false,
    prospectType: '', priority: '', nextFollowUp: defaultFollowUp(),
    assignTo: brokerName || '',
    // Requirement fields (optional, within queue section)
    showRequirements: false,
    sfMin: '', sfMax: '', acresMin: '', acresMax: '', priceMin: '', priceMax: '',
  });

  // Searchable lead source state
  const [leadSourceSearch, setLeadSourceSearch] = useState('');
  const [showLeadSourceList, setShowLeadSourceList] = useState(false);
  const [showMarketingSection, setShowMarketingSection] = useState(false);

  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(false);
  const [emailWarning, setEmailWarning] = useState(false);
  const emailInputRef = useRef(null);

  const searchInputRef = useRef(null);
  const debounceRef    = useRef(null);

  // Auto-focus search input
  useEffect(() => { searchInputRef.current?.focus(); }, []);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearched(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchContacts(q);
        setResults(res);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
        setSearched(true);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleFormChange = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setError('');
  }, []);

  function handleSelectExisting(contact) {
    setSelectedContact(contact);
    setStep('found');
    setLoadingDetail(true);
    setContactDetail(null);
    setCallHistory([]);
    setContactNotes([]);
    setShowLogForm(false);
    setShowQueueForm(false);
    setLogSuccess(false);
    setLogForm({ activityType: '', outcome: '', notes: '' });
    Promise.all([
      fetchContactDetails(contact.id),
      fetchCallHistory(contact.id),
      fetchContactUpdates(contact.id),
    ]).then(([detail, history, notes]) => {
      setContactDetail(detail);
      setCallHistory(history);
      setContactNotes(notes);
    }).catch(() => {}).finally(() => setLoadingDetail(false));
  }

  function handleAddNew() {
    setForm((f) => ({ ...f, name: query }));
    setStep('new');
  }

  // Save new contact (+ optional prospect record)
  async function handleSaveNew() {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (form.addToQueue && !form.prospectType) { setError('Please select a prospect type.'); return; }
    if (form.addToQueue && !form.priority) { setError('Please select a priority.'); return; }

    // Soft warning: content preferences selected but no email
    if (form.contentPreferences.length > 0 && !form.email.trim() && !emailWarning) {
      setEmailWarning(true);
      return;
    }
    setEmailWarning(false);

    setSaving(true);
    setError('');
    try {
      const contact = await createContact({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        title: form.title.trim(),
        leadSource: form.leadSource,
        advisorCreator: brokerName || '',
        contactType: form.contactType,
        persona: form.persona,
        interests: form.interests,
        markets: form.markets,
        contentPreferences: form.contentPreferences,
      });
      if (form.addToQueue && contact?.id) {
        const prospect = await createProspectRecord(contact.id, form.name.trim(), {
          prospectType: form.prospectType,
          priority: form.priority,
          nextFollowUp: form.nextFollowUp,
          assignedBrokerName: form.assignTo || brokerName,
        });
        const hasReqs = form.sfMin || form.sfMax || form.acresMin || form.acresMax || form.priceMin || form.priceMax;
        if (prospect?.id && hasReqs) {
          await createSubitem(prospect.id, {
            status: 'Looking',
            sfMin: form.sfMin, sfMax: form.sfMax,
            acresMin: form.acresMin, acresMax: form.acresMax,
            priceMin: form.priceMin, priceMax: form.priceMax,
          });
        }
      }
      setSuccess(true);
      setTimeout(() => { onAdded?.(); onClose(); }, 1400);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Log a call for existing contact (no prospecting record needed)
  async function handleLogCall() {
    if (!logForm.activityType) { setError('Select an activity type.'); return; }
    if (!logForm.outcome)      { setError('Select an outcome.'); return; }
    setLogSaving(true);
    setError('');
    try {
      const today = new Date();
      const nextFollowUp = new Date(today);
      nextFollowUp.setDate(today.getDate() + 7);
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      await createActivityLogEntry({
        contactItemId: selectedContact.id,
        prospectingItemName: contactDetail?.name || selectedContact.name,
        activityType: logForm.activityType,
        outcome: logForm.outcome,
        notes: logForm.notes,
        nextFollowUpDate: fmt(nextFollowUp),
        stageAtLog: '',
      });
      setLogSuccess(true);
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setLogSaving(false);
    }
  }

  // Add existing contact to queue
  async function handleAddExistingToQueue() {
    if (!form.prospectType) { setError('Please select a prospect type.'); return; }
    if (!form.priority)     { setError('Please select a priority.'); return; }

    setSaving(true);
    setError('');
    try {
      const prospect = await createProspectRecord(selectedContact.id, selectedContact.name, {
        prospectType: form.prospectType,
        priority: form.priority,
        nextFollowUp: form.nextFollowUp,
        assignedBrokerName: form.assignTo || brokerName,
      });
      const hasReqs = form.sfMin || form.sfMax || form.acresMin || form.acresMax || form.priceMin || form.priceMax;
      if (prospect?.id && hasReqs) {
        await createSubitem(prospect.id, {
          status: 'Looking',
          sfMin: form.sfMin, sfMax: form.sfMax,
          acresMin: form.acresMin, acresMax: form.acresMax,
          priceMin: form.priceMin, priceMax: form.priceMax,
        });
      }
      setSuccess(true);
      setTimeout(() => { onAdded?.(); onClose(); }, 1400);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Overlay click closes
  function handleOverlay(e) { if (e.target === e.currentTarget) onClose(); }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      onClick={handleOverlay}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.48)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: '720px',
          backgroundColor: C.bgCard,
          borderRadius: '20px 20px 0 0',
          maxHeight: '93vh', overflowY: 'auto',
          paddingBottom: '40px',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 6px' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', backgroundColor: C.border }} />
        </div>

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 20px 14px', borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {(step === 'found' || step === 'new' || step === 'add-to-queue') && (
              <button
                onClick={() => { setStep('search'); setError(''); setSuccess(false); setSelectedContact(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: C.textSecondary, padding: '0 2px', lineHeight: 1 }}
              >
                ←
              </button>
            )}
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: C.textPrimary }}>
              {step === 'search' && 'Find or Add Contact'}
              {step === 'found'  && (contactDetail?.name || selectedContact?.name || 'Contact Found')}
              {step === 'new'    && 'New Contact'}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: C.bgSoft, border: 'none', borderRadius: '50%',
              width: '30px', height: '30px', fontSize: '18px',
              color: C.textSecondary, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* ── STEP: SEARCH ─────────────────────────────────────────────── */}
        {step === 'search' && (
          <div style={{ padding: '20px' }}>
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <span style={{
                position: 'absolute', left: '13px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '15px',
                color: C.textTertiary, pointerEvents: 'none',
              }}>
                🔍
              </span>
              <input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a name to search…"
                style={{ ...inputStyle, paddingLeft: '40px', marginBottom: 0, fontSize: '15px' }}
              />
            </div>

            {searching && (
              <p style={{ textAlign: 'center', color: C.textTertiary, fontSize: '14px', padding: '28px 0' }}>
                Searching…
              </p>
            )}

            {!searching && results.length > 0 && (
              <>
                <p style={{ ...labelStyle, marginBottom: '10px' }}>In your contacts</p>
                {results.map((contact) => {
                  const phone   = contact.column_values?.find((c) => c.id === 'phone')?.text   || '';
                  const company = contact.column_values?.find((c) => c.id === 'text7')?.text   || '';
                  return (
                    <div
                      key={contact.id}
                      onClick={() => handleSelectExisting(contact)}
                      style={{
                        padding: '12px 14px', borderRadius: '10px',
                        border: `1px solid ${C.border}`, marginBottom: '8px',
                        cursor: 'pointer', backgroundColor: C.bgCard,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: C.textPrimary }}>{contact.name}</div>
                        {(company || phone) && (
                          <div style={{ fontSize: '12px', color: C.textTertiary, marginTop: '2px' }}>
                            {[company, phone].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                      <span style={{ color: C.textTertiary, fontSize: '16px' }}>›</span>
                    </div>
                  );
                })}
              </>
            )}

            {!searching && searched && results.length === 0 && query.trim().length >= 2 && (
              <div style={{ textAlign: 'center', padding: '32px 20px' }}>
                <p style={{ fontSize: '14px', color: C.textSecondary, marginBottom: '18px' }}>
                  No match for "<strong>{query}</strong>"
                </p>
                <button
                  onClick={handleAddNew}
                  style={{
                    backgroundColor: C.indigo, color: '#fff',
                    border: 'none', borderRadius: '12px',
                    padding: '13px 28px', fontSize: '15px',
                    fontWeight: '600', cursor: 'pointer',
                  }}
                >
                  ＋ Add "{query}" as new contact
                </button>
              </div>
            )}

            {!searching && !searched && (
              <p style={{ textAlign: 'center', color: C.textTertiary, fontSize: '13px', padding: '36px 0' }}>
                Start typing to check if they're already in your contacts
              </p>
            )}
          </div>
        )}

        {/* ── STEP: FOUND ──────────────────────────────────────────────── */}
        {step === 'found' && selectedContact && (
          <div style={{ padding: '20px' }}>
            {loadingDetail ? (
              <p style={{ textAlign: 'center', color: C.textTertiary, padding: '40px 0', fontSize: '14px' }}>Loading contact…</p>
            ) : (
              <>
                {/* Contact info card */}
                <div style={{
                  backgroundColor: C.bgSoft, borderRadius: '14px',
                  padding: '16px 18px', marginBottom: '20px',
                }}>
                  <p style={{ margin: '0 0 10px', fontWeight: '700', fontSize: '18px', color: C.textPrimary }}>
                    {contactDetail?.name || selectedContact.name}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {contactDetail?.company && (
                      <span style={{ fontSize: '13px', color: C.textSecondary }}>🏢 {contactDetail.company}</span>
                    )}
                    {contactDetail?.phone && (
                      <a href={`tel:${contactDetail.phone}`} style={{ fontSize: '13px', color: C.indigo, textDecoration: 'none' }}>
                        📞 {contactDetail.phone}
                      </a>
                    )}
                    {contactDetail?.email && (
                      <a href={`mailto:${contactDetail.email}`} style={{ fontSize: '13px', color: C.indigo, textDecoration: 'none' }}>
                        ✉️ {contactDetail.email}
                      </a>
                    )}
                    {contactDetail?.notes && (
                      <p style={{ margin: '6px 0 0', fontSize: '12px', color: C.textSecondary, fontStyle: 'italic' }}>
                        {contactDetail.notes}
                      </p>
                    )}
                  </div>
                </div>

                {/* Marketing info — collapsible */}
                {contactDetail && (
                  <div style={{ marginBottom: '16px' }}>
                    <div
                      onClick={() => setShowMarketingSection((v) => !v)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                        backgroundColor: showMarketingSection ? C.indigoLight : C.bgSoft,
                        border: `1px solid ${showMarketingSection ? C.indigo : C.border}`,
                      }}
                    >
                      <span style={{ fontSize: '13px', fontWeight: '600', color: showMarketingSection ? C.indigoDark : C.textSecondary }}>
                        📋 Marketing Info
                      </span>
                      <span style={{ fontSize: '12px', color: C.textTertiary }}>{showMarketingSection ? '▲' : '▼'}</span>
                    </div>
                    {showMarketingSection && (
                      <div style={{
                        backgroundColor: C.bgCard, border: `1px solid ${C.border}`,
                        borderTop: 'none', borderRadius: '0 0 10px 10px',
                        padding: '12px 14px',
                      }}>
                        {[
                          { label: 'Lead Source',         value: contactDetail.leadSource },
                          { label: 'Contact Type',        value: contactDetail.contactType },
                          { label: 'Persona',             value: contactDetail.persona },
                          { label: 'Interests',           value: contactDetail.interests },
                          { label: 'Markets',             value: contactDetail.markets },
                          { label: 'Content Preferences', value: contactDetail.contentPreferences },
                          { label: 'Advisor',             value: contactDetail.advisorCreator },
                        ].map(({ label, value }) => value ? (
                          <div key={label} style={{ display: 'flex', gap: '8px', marginBottom: '6px', fontSize: '12px' }}>
                            <span style={{ color: C.textTertiary, minWidth: '120px', fontWeight: '600' }}>{label}</span>
                            <span style={{ color: C.textPrimary }}>{value}</span>
                          </div>
                        ) : null)}
                        {!['leadSource','contactType','persona','interests','markets','contentPreferences','advisorCreator']
                          .some(k => contactDetail[k]) && (
                          <p style={{ margin: 0, fontSize: '12px', color: C.textTertiary, fontStyle: 'italic' }}>
                            No marketing fields filled in yet.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Combined activity feed: call history + monday updates + historical notes */}
                {(() => {
                  const feed = [
                    ...callHistory.map(e => ({ ...e, feedType: 'call' })),
                    ...contactNotes.map(e => ({ ...e, feedType: 'update' })),
                    ...(contactDetail?.notes ? [{ date: '', text: contactDetail.notes, author: 'Historical note', feedType: 'note' }] : []),
                  ].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

                  if (!feed.length) return null;
                  return (
                    <div style={{ marginBottom: '20px' }}>
                      <p style={labelStyle}>Activity & Notes</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {feed.map((entry, i) => {
                          const oc = entry.feedType === 'call'
                            ? (OUTCOME_COLORS[entry.outcome] || { bg: C.bgSoft, color: C.textSecondary })
                            : entry.feedType === 'note'
                              ? { bg: C.amberLight, color: C.amber }
                              : { bg: C.bgSoft, color: C.textSecondary };
                          return (
                            <div key={i} style={{
                              backgroundColor: C.bgCard, borderRadius: '10px',
                              padding: '10px 14px', border: `1px solid ${C.border}`,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                  {entry.feedType === 'call' && (
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: C.textPrimary }}>{entry.activityType}</span>
                                  )}
                                  <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px', backgroundColor: oc.bg, color: oc.color }}>
                                    {entry.feedType === 'call' ? entry.outcome : entry.feedType === 'note' ? 'Note' : 'Update'}
                                  </span>
                                  {entry.author && entry.feedType !== 'call' && (
                                    <span style={{ fontSize: '11px', color: C.textTertiary }}>{entry.author}</span>
                                  )}
                                </div>
                                {entry.date && <span style={{ fontSize: '11px', color: C.textTertiary }}>{entry.date}</span>}
                              </div>
                              {entry.text && <p style={{ margin: 0, fontSize: '12px', color: C.textSecondary }}>{entry.text}</p>}
                              {entry.notes && <p style={{ margin: 0, fontSize: '12px', color: C.textSecondary }}>{entry.notes}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Log a Call ─────────────────────────────────────────── */}
                {!logSuccess ? (
                  <>
                    <button
                      onClick={() => setShowLogForm((v) => !v)}
                      style={{
                        width: '100%', padding: '13px', borderRadius: '12px',
                        backgroundColor: showLogForm ? C.bgSoft : C.indigo,
                        color: showLogForm ? C.textSecondary : '#fff',
                        border: showLogForm ? `1.5px solid ${C.border}` : 'none',
                        fontSize: '15px', fontWeight: '600', cursor: 'pointer', marginBottom: '10px',
                      }}
                    >
                      {showLogForm ? 'Cancel' : '📋 Log a Call'}
                    </button>

                    {showLogForm && (
                      <div style={{
                        backgroundColor: C.bgSoft, borderRadius: '12px',
                        padding: '16px', marginBottom: '10px',
                        border: `1.5px solid ${C.border}`,
                      }}>
                        {error && <ErrorBanner message={error} />}

                        <label style={labelStyle}>Activity Type *</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                          {ACTIVITY_TYPES.map((t) => (
                            <button key={t} onClick={() => { setLogForm((f) => ({ ...f, activityType: t })); setError(''); }}
                              style={{
                                padding: '7px 13px', borderRadius: '20px', fontSize: '12px', fontWeight: '500',
                                cursor: 'pointer', border: 'none',
                                backgroundColor: logForm.activityType === t ? C.indigo : C.bgCard,
                                color: logForm.activityType === t ? '#fff' : C.textSecondary,
                              }}>{t}</button>
                          ))}
                        </div>

                        <label style={labelStyle}>Outcome *</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                          {OUTCOMES.map((o) => {
                            const oc = OUTCOME_COLORS[o] || { bg: C.bgCard, color: C.textSecondary };
                            const selected = logForm.outcome === o;
                            return (
                              <button key={o} onClick={() => { setLogForm((f) => ({ ...f, outcome: o })); setError(''); }}
                                style={{
                                  padding: '7px 13px', borderRadius: '20px', fontSize: '12px', fontWeight: '500',
                                  cursor: 'pointer', border: `1.5px solid ${selected ? oc.color : C.border}`,
                                  backgroundColor: selected ? oc.bg : C.bgCard,
                                  color: selected ? oc.color : C.textSecondary,
                                }}>{o}</button>
                            );
                          })}
                        </div>

                        <label style={labelStyle}>Notes</label>
                        <textarea
                          value={logForm.notes}
                          onChange={(e) => setLogForm((f) => ({ ...f, notes: e.target.value }))}
                          placeholder="What was discussed?"
                          rows={3}
                          style={{
                            ...inputStyle, resize: 'vertical', marginBottom: '14px',
                            fontFamily: 'inherit',
                          }}
                        />

                        <SaveButton onClick={handleLogCall} saving={logSaving} label="Save Log" />
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{
                    backgroundColor: C.tealLight, borderRadius: '12px',
                    padding: '14px 18px', marginBottom: '10px', textAlign: 'center',
                  }}>
                    <span style={{ fontSize: '20px' }}>✅</span>
                    <p style={{ margin: '6px 0 0', fontWeight: '600', color: C.tealDark, fontSize: '14px' }}>Call logged!</p>
                  </div>
                )}

                {/* ── Add to Queue (subdued, at bottom) ──────────────────── */}
                <div
                  onClick={() => setShowQueueForm((v) => !v)}
                  style={{
                    marginTop: '6px', padding: '12px 16px', borderRadius: '12px',
                    border: `1.5px dashed ${showQueueForm ? C.indigo : C.border}`,
                    backgroundColor: showQueueForm ? C.indigoLight : 'transparent',
                    cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  <span style={{ fontSize: '13px', fontWeight: '600', color: showQueueForm ? C.indigoDark : C.textTertiary }}>
                    {showQueueForm ? '▲ Hide' : '＋ Add to Prospecting Queue'}
                  </span>
                </div>

                {showQueueForm && (
                  <div style={{ marginTop: '12px' }}>
                    {error && !showLogForm && <ErrorBanner message={error} />}
                    <QueueFields form={form} onChange={handleFormChange} />
                    <SaveButton onClick={handleAddExistingToQueue} saving={saving} label="Add to Queue" />
                    {success && <SuccessBanner message="Added to queue!" />}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── STEP: NEW CONTACT ─────────────────────────────────────────── */}
        {step === 'new' && (
          <div style={{ padding: '20px' }}>
            {success ? (
              <SuccessBanner message={form.addToQueue ? 'Added to queue!' : 'Contact saved!'} />
            ) : (
              <>
                {error && <ErrorBanner message={error} />}

                {/* Contact info */}
                <label style={labelStyle}>Full Name *</label>
                <input style={inputStyle} value={form.name} placeholder="e.g. Jane Smith"
                  onChange={(e) => handleFormChange('name', e.target.value)} />

                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} value={form.phone} placeholder="(406) 555-0100" type="tel"
                  onChange={(e) => handleFormChange('phone', e.target.value)} />

                <label style={labelStyle}>Email</label>
                <input ref={emailInputRef} style={inputStyle} value={form.email} placeholder="jane@example.com" type="email"
                  onChange={(e) => { handleFormChange('email', e.target.value); setEmailWarning(false); }} />

                <label style={labelStyle}>Company</label>
                <input style={inputStyle} value={form.company} placeholder="Company name"
                  onChange={(e) => handleFormChange('company', e.target.value)} />

                <label style={labelStyle}>Title</label>
                <input style={{ ...inputStyle, marginBottom: '20px' }} value={form.title} placeholder="e.g. VP of Acquisitions"
                  onChange={(e) => handleFormChange('title', e.target.value)} />

                {/* Lead source — searchable, visually prominent */}
                <div style={{
                  backgroundColor: '#FFFBEB', border: '1.5px solid #FCD34D',
                  borderRadius: '12px', padding: '14px 16px', marginBottom: '16px',
                }}>
                  <p style={{ margin: '0 0 3px', fontSize: '11px', fontWeight: '700', color: '#92400E', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    📍 Where did this lead come from?
                  </p>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#A16207' }}>
                    Helps marketing track what's working
                  </p>
                  {form.leadSource ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ padding: '6px 14px', borderRadius: '20px', backgroundColor: '#F59E0B', color: '#fff', fontSize: '13px', fontWeight: '600' }}>
                        {form.leadSource}
                      </span>
                      <button onClick={() => { handleFormChange('leadSource', ''); setLeadSourceSearch(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400E', fontSize: '13px' }}>
                        ✕ Change
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        value={leadSourceSearch}
                        onChange={(e) => { setLeadSourceSearch(e.target.value); setShowLeadSourceList(true); }}
                        onFocus={() => setShowLeadSourceList(true)}
                        placeholder="Type to search sources…"
                        style={{ ...inputStyle, marginBottom: '6px', backgroundColor: '#FFFBEB', border: '1.5px solid #FCD34D' }}
                      />
                      {showLeadSourceList && (
                        <div style={{ maxHeight: '160px', overflowY: 'auto', borderRadius: '8px', border: `1px solid ${C.border}`, backgroundColor: C.bgCard }}>
                          {LEAD_SOURCES.filter(s => s.toLowerCase().includes(leadSourceSearch.toLowerCase())).map(src => (
                            <div key={src} onClick={() => { handleFormChange('leadSource', src); setLeadSourceSearch(''); setShowLeadSourceList(false); }}
                              style={{ padding: '9px 13px', fontSize: '13px', cursor: 'pointer', color: C.textPrimary,
                                borderBottom: `1px solid ${C.border}` }}>
                              {src}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Marketing fields */}
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ ...labelStyle, marginBottom: '10px', color: C.indigo }}>Marketing Info — helps the team target outreach</p>

                  <label style={labelStyle}>Contact Type</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '14px' }}>
                    {CONTACT_TYPES.map(t => (
                      <button key={t} onClick={() => handleFormChange('contactType', form.contactType === t ? '' : t)}
                        style={{ padding: '5px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: '500', cursor: 'pointer', border: 'none',
                          backgroundColor: form.contactType === t ? C.indigo : C.bgSoft,
                          color: form.contactType === t ? '#fff' : C.textSecondary }}>
                        {t}
                      </button>
                    ))}
                  </div>

                  <label style={labelStyle}>Persona (select all that apply)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '14px' }}>
                    {PERSONAS.map(p => {
                      const sel = (form.persona || []).includes(p);
                      return (
                        <button key={p} onClick={() => handleFormChange('persona', sel ? form.persona.filter(x => x !== p) : [...(form.persona || []), p])}
                          style={{ padding: '5px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: '500', cursor: 'pointer', border: 'none',
                            backgroundColor: sel ? C.indigo : C.bgSoft,
                            color: sel ? '#fff' : C.textSecondary }}>
                          {p}
                        </button>
                      );
                    })}
                  </div>

                  <label style={labelStyle}>Interests (select all that apply)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '14px' }}>
                    {INTERESTS_OPTIONS.map(opt => {
                      const sel = form.interests.includes(opt);
                      return (
                        <button key={opt} onClick={() => handleFormChange('interests', sel ? form.interests.filter(x => x !== opt) : [...form.interests, opt])}
                          style={{ padding: '5px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: '500', cursor: 'pointer', border: 'none',
                            backgroundColor: sel ? C.teal : C.bgSoft, color: sel ? '#fff' : C.textSecondary }}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>

                  <label style={labelStyle}>Markets (select all that apply)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '14px' }}>
                    {MARKETS_OPTIONS.map(opt => {
                      const sel = form.markets.includes(opt);
                      return (
                        <button key={opt} onClick={() => handleFormChange('markets', sel ? form.markets.filter(x => x !== opt) : [...form.markets, opt])}
                          style={{ padding: '5px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: '500', cursor: 'pointer', border: 'none',
                            backgroundColor: sel ? C.teal : C.bgSoft, color: sel ? '#fff' : C.textSecondary }}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>

                  <label style={labelStyle}>Content Preferences</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '4px' }}>
                    {CONTENT_PREFS.map(opt => {
                      const sel = form.contentPreferences.includes(opt);
                      return (
                        <button key={opt} onClick={() => handleFormChange('contentPreferences', sel ? form.contentPreferences.filter(x => x !== opt) : [...form.contentPreferences, opt])}
                          style={{ padding: '8px 13px', borderRadius: '9px', fontSize: '12px', fontWeight: '500', cursor: 'pointer',
                            border: `1.5px solid ${sel ? C.indigo : C.border}`, textAlign: 'left',
                            backgroundColor: sel ? C.indigoLight : C.bgCard,
                            color: sel ? C.indigoDark : C.textSecondary }}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Add to queue toggle */}
                <div
                  onClick={() => handleFormChange('addToQueue', !form.addToQueue)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
                    backgroundColor: form.addToQueue ? C.indigoLight : C.bgSoft,
                    border: `1.5px solid ${form.addToQueue ? C.indigo : C.border}`,
                    marginBottom: form.addToQueue ? '16px' : '28px',
                    transition: 'background 0.15s, border 0.15s',
                  }}
                >
                  <div>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: C.textPrimary }}>
                      Add to prospecting queue
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: C.textSecondary }}>
                      Set type, priority, and first follow-up date
                    </p>
                  </div>
                  <Toggle on={form.addToQueue} />
                </div>

                {form.addToQueue && (
                  <QueueFields form={form} onChange={handleFormChange} />
                )}

                {emailWarning && (
                  <div style={{
                    backgroundColor: '#FFFBEB', border: '1.5px solid #FCD34D',
                    borderRadius: '12px', padding: '14px 16px', marginBottom: '12px',
                  }}>
                    <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#92400E', fontWeight: '600' }}>
                      ⚠️ No email address — they won't receive any marketing content
                    </p>
                    <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#A16207' }}>
                      You've selected content preferences but haven't added an email. Add one now or save anyway.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => { setEmailWarning(false); emailInputRef.current?.focus(); emailInputRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '9px', fontSize: '13px',
                          fontWeight: '600', cursor: 'pointer', border: 'none',
                          backgroundColor: '#F59E0B', color: '#fff',
                        }}
                      >
                        Add Email First
                      </button>
                      <button
                        onClick={() => { setEmailWarning(false); handleSaveNew(); }}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '9px', fontSize: '13px',
                          fontWeight: '500', cursor: 'pointer',
                          border: `1px solid #FCD34D`, backgroundColor: 'transparent', color: '#92400E',
                        }}
                      >
                        Save Anyway
                      </button>
                    </div>
                  </div>
                )}

                <SaveButton
                  onClick={handleSaveNew}
                  saving={saving}
                  label={form.addToQueue ? 'Save & Add to Queue' : 'Save Contact'}
                />
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Small reusable pieces ──────────────────────────────────────────────────

function Toggle({ on }) {
  return (
    <div style={{
      width: '44px', height: '24px', borderRadius: '12px', flexShrink: 0,
      backgroundColor: on ? C.indigo : C.border, position: 'relative',
      transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute', top: '3px',
        left: on ? '23px' : '3px',
        width: '18px', height: '18px', borderRadius: '50%',
        backgroundColor: '#fff', transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function SaveButton({ onClick, saving, label }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      style={{
        width: '100%', padding: '15px', marginTop: '4px',
        backgroundColor: saving ? C.border : C.indigo,
        color: saving ? C.textTertiary : '#fff',
        border: 'none', borderRadius: '12px',
        fontSize: '15px', fontWeight: '700',
        cursor: saving ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s',
      }}
    >
      {saving ? 'Saving…' : label}
    </button>
  );
}

function ErrorBanner({ message }) {
  return (
    <div style={{
      backgroundColor: C.redLight, borderRadius: '9px',
      padding: '10px 14px', marginBottom: '14px',
      fontSize: '13px', color: C.red,
    }}>
      {message}
    </div>
  );
}

function SuccessBanner({ message }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 20px' }}>
      <div style={{ fontSize: '44px', marginBottom: '14px' }}>✅</div>
      <p style={{ fontSize: '17px', fontWeight: '700', color: C.textPrimary, margin: 0 }}>{message}</p>
    </div>
  );
}
